/*
 * THE TABLE SCREEN'S SOURCE SEAM — the Cardinal Rule boundary in file form.
 *
 * Components never fetch: a widget calls an injected source; only the source
 * knows the transport. Today that transport is the BackendClient facade (whose
 * calls already execute in the Backend Worker); Phase 4.4 moves the remaining
 * screen IO fully worker-side, and because every table-screen call goes through
 * THIS seam, that move rewrites this one file and no component.
 *
 * The bodies below are the table-viewport's four direct calls moved verbatim —
 * a pure relocation, zero behavior change. The endpoint screens keep their
 * direct calls until their Step-6 rewrite adopts the same seam (rewriting those
 * files twice would be churn, not progress).
 */
import type { DataGridColumn, DataGridPage } from '@vitesse/base-ui/elements/vitesse-data-grid';
import type { BackendClient } from '../../bridge/backend-client';
import type { IntegrationRowPage, RowPredicateLite } from '../../../core/bridge/connect-client';

export interface TableSource {
  /** One-row schema probe: the first table's path, or undefined while the
   *  connector isn't queryable yet (the screen retries on that). */
  probeFirstTable(): Promise<string | undefined>;
  /** One navigator page of TABLES (search-scoped), already grid-shaped. */
  tables(cursor: string, search: string, limit: number): Promise<DataGridPage>;
  /** One table's columns (the grid headers), loaded lazily on select. */
  columns(table: string): Promise<TableColumn[]>;
  /** One keyset page of the table's ROWS, carrying the active WHERE filter. */
  rows(table: string, cursor: string, limit: number, filter?: RowPredicateLite[]): Promise<IntegrationRowPage>;
  /** Change one EXISTING row in place — the in-cell edit. `where` must address
   *  exactly one row; the source refuses (and rolls back) if it does not. */
  updateRow(
    table: string,
    where: RowPredicateLite[],
    values: Record<string, string | number | boolean | null>,
  ): Promise<{ affected: number }>;
}

/** A grid column plus the schema flag that decides whether its cells may be
 *  edited. `writable` is false for a view's columns and for any source that
 *  reports the column read-only — the schema has carried it all along. */
export interface TableColumn extends DataGridColumn {
  writable: boolean;
}

export function tableSource(backend: BackendClient, gatewayId: string, integrationId: string): TableSource {
  return {
    async probeFirstTable() {
      const page = await backend.api.integrationBrowseSchema(gatewayId, integrationId, '', '', '', 1);
      return page.nodes[0]?.path;
    },
    async tables(cursor, search, limit) {
      const page = await backend.api.integrationBrowseSchema(gatewayId, integrationId, '', cursor, search, limit);
      return {
        rows: page.nodes.map((n) => ({ name: n.path, cols: n.childCount })),
        nextCursor: page.nextCursor,
        prevCursor: page.prevCursor,
      };
    },
    async columns(table) {
      const page = await backend.api.integrationBrowseSchema(gatewayId, integrationId, table, '', '', 0);
      return page.nodes.map((n) => {
        const key = n.path.slice(n.path.indexOf('/') + 1);
        return { key, label: key, type: n.dataType, writable: n.writable };
      });
    },
    async rows(table, cursor, limit, filter) {
      return backend.api.integrationReadRows(gatewayId, integrationId, table, cursor, limit, filter);
    },
    async updateRow(table, where, values) {
      return backend.api.integrationUpdateRow(gatewayId, integrationId, table, where, values);
    },
  };
}
